import React from 'react'
import type { Block, Variable } from '../../types'
import { PropertyLabel, PropertyInput } from './shared'

interface NumberInputPropertiesProps {
  block: Block
  variable: Variable
  linkedBlocks: Block[]
  onUpdateBlock: (id: string, updates: Partial<Block>) => void
  onUpdateVariable: (name: string, updates: Partial<Variable>) => void
  onRenameVariable: (oldName: string, newName: string) => void
}

export default function NumberInputProperties({
  block,
  variable,
  linkedBlocks,
  onUpdateBlock,
  onUpdateVariable,
  onRenameVariable,
}: NumberInputPropertiesProps) {
  const config = (block.config || {}) as Record<string, unknown>

  return (
    <>
      <div className="space-y-2">
        <PropertyLabel>Label</PropertyLabel>
        <PropertyInput
          value={block.label || ''}
          onChange={(e) => onUpdateBlock(block.id, { label: e.target.value })}
        />
      </div>

      <div className="space-y-2">
        <PropertyLabel>Linked Variable</PropertyLabel>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-[#5048e5] font-bold">@</span>
          <input
            type="text"
            value={variable.name}
            onChange={(e) =>
              onRenameVariable(variable.name, e.target.value.replace(/[^a-z0-9_]/gi, '_').toLowerCase())
            }
            className="w-full font-mono bg-slate-50 border border-slate-200 rounded-lg p-2.5 pl-7 text-sm focus:ring-2 focus:ring-[#5048e5] focus:border-transparent transition-all outline-none"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <PropertyLabel>Min Value</PropertyLabel>
          <PropertyInput
            type="number"
            value={variable.min ?? ''}
            onChange={(e) => onUpdateVariable(variable.name, { min: e.target.value === '' ? undefined : Number(e.target.value) } as Partial<Variable>)}
          />
        </div>
        <div className="space-y-2">
          <PropertyLabel>Max Value</PropertyLabel>
          <PropertyInput
            type="number"
            value={variable.max ?? ''}
            onChange={(e) => onUpdateVariable(variable.name, { max: e.target.value === '' ? undefined : Number(e.target.value) } as Partial<Variable>)}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <PropertyLabel>Step</PropertyLabel>
          <PropertyInput
            type="number"
            value={variable.step ?? 1}
            min={0.01}
            onChange={(e) => onUpdateVariable(variable.name, { step: Number(e.target.value) })}
          />
        </div>
        <div className="space-y-2">
          <PropertyLabel>Placeholder</PropertyLabel>
          <PropertyInput
            value={(config.placeholder as string) || ''}
            onChange={(e) => onUpdateBlock(block.id, { config: { ...config, placeholder: e.target.value } })}
            placeholder="0"
          />
        </div>
      </div>

      <div className="space-y-2">
        <PropertyLabel>Suffix</PropertyLabel>
        <PropertyInput
          value={(config.suffix as string) || ''}
          onChange={(e) => onUpdateBlock(block.id, { config: { ...config, suffix: e.target.value } })}
          placeholder="e.g. €, %, users..."
        />
      </div>

      {linkedBlocks.length > 0 && (
        <div className="pt-4 border-t border-slate-100">
          <PropertyLabel>Linked Blocks</PropertyLabel>
          <ul className="space-y-1 mt-2">
            {linkedBlocks.map((lb) => (
              <li key={lb.id} className="text-sm text-slate-500">
                &bull; {lb.content?.slice(0, 30) || lb.label || lb.type}{' '}
                <span className="text-xs">({lb.type})</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  )
}
